import Vue from "vue"
import Router from "vue-router"
import store from "./store"
import auth from "@/adapter/auth"
import {
  getAuthInstance,
  popPostAuthRedirectPath,
  setPostAuthRedirectPath
} from "@hylable/auth"

Vue.use(Router)

const router = new Router({
  routes: [
    {
      meta: {isPublic: true},
      path: "/login",
      name: "login",
      component: () => import("@/views/Login.vue"),
    },
    {
      meta: {isPublic: true},
      path: "/lti/login",
      name: "lti-login",
      component: () => import("@/views/lti/Login.vue"),
    },
    {
      path: "/lti/login-member-register",
      name: "lti-login-member-register",
      component: () => import("@/views/lti/LoginMemberRegister.vue"),
    },
    {
      path: "/lti/content-select",
      name: "lti-content-select",
      component: () => import("@/views/lti/ContentSelect.vue"),
    },
    {
      path: "/",
      name: "course-list",
      component: () => import("@/views/CourseList.vue"),
      alias: "/course",
    },
    {
      meta: {breadcrumbs: ["Home", ":cid", "Discussion list"]},
      path: "/course/:cid/discussion",
      name: "discussion-list",
      component: () => import("@/views/DiscussionList.vue"),
    },
    {
      meta: {breadcrumbs: ["Home", "Management"]},
      path: "/manage",
      name: "management",
      component: () => import("@/views/Management.vue")
    },
    {
      meta: {breadcrumbs: ["Home", "Management", "Member"]},
      path: "/manage/member",
      name: "member-management",
      component: () => import("@/views/MemberManagement.vue")
    },
    {
      meta: {breadcrumbs: ["Home", "Management", "Recorder"]},
      path: "/manage/recorder",
      name: "recorder-management",
      component: () => import("@/views/RecorderManagement.vue")
    },
    {
      meta: {breadcrumbs: ["Home", "Management", "Data"]},
      path: "/manage/data",
      name: "data-management",
      component: () => import("@/views/DataManagement.vue")
    },
    {
      meta: {breadcrumbs: ["Home", "Management", "External"]},
      path: "/manage/external",
      name: "external-management",
      component: () => import("@/views/ExternalManagement.vue")
    },
    {
      meta: {breadcrumbs: ["Home", "Management", "External", "LMS linkage setting"]},
      path: "/manage/external/lms",
      name: "lti-platform-register",
      component: () => import("@/views/lti/PlatformRegister.vue"),
    },
    {
      meta: {breadcrumbs: ["Home", "Management", "External", "LRS linkage setting"]},
      path: "/manage/external/lrs",
      name: "xapi-register",
      component: () => import("@/views/xapi/Register.vue"),
    },
    {
      meta: {breadcrumbs: ["Home", "Management", "Config"]},
      path: "/manage/config",
      name: "config",
      component: () => import("@/views/Config.vue")
    },
    {
      meta: {breadcrumbs: ["Home", ":cid", "Course info"]},
      path: "/course/:cid/stats",
      name: "course",
      component: () => import("@/views/Course.vue")
    },
    {
      meta: {breadcrumbs: ["Home", ":cid", "Member list"]},
      path: "/course/:cid/member",
      name: "course-member-list",
      component: () => import("@/views/CourseMemberList.vue")
    },
    {
      meta: {breadcrumbs: ["Home", ":cid", "Member list", ":mid"]},
      path: "/course/:cid/member/:mid",
      name: "course-member",
      component: () => import("@/views/CourseMember.vue")
    },
    {
      meta: {breadcrumbs: ["Home", ":cid", "Discussion list", ":did"]},
      path: "/course/:cid/discussion/:did",
      name: "discussion",
      component: () => import("@/views/Discussion.vue")
    },
    {
      meta: {breadcrumbs: ["Home", ":cid", "Discussion list", ":did", "DiscussionMode.Behavior"]},
      path: "/course/:cid/discussion/:did/skill",
      name: "discussion-skill",
      component: () => import("@/views/DiscussionSkill.vue")
    },
    {
      path: "/course/:cid", redirect: {path: "/course/:cid/discussion"},
    },
    {
      meta: {breadcrumbs: ["Home", "User setting"]},
      path: "/user-setting",
      name: "user-setting",
      component: () => import("@/views/UserSetting.vue"),
    },
    {
      path: "*", redirect: {path: "/"},
    },
  ],
  scrollBehavior: (to, from, savedPosition) => savedPosition || { x: 0, y: 0 }
})

async function handleRedirectLogin(to, next) {
  // redirect to one of the following logins:
  //   - LTI login if "ltik" param is present, or
  //   - auth-broker login if condition in hylable-sdk matches, or
  //   - local login otherwise

  // remember the original destination to redirect after login
  if (to.name !== "login") setPostAuthRedirectPath(to.fullPath)

  if(to.query.ltik) {
    return next({name: "lti-login", query: to.query})
  }

  if(getAuthInstance()?.isAuthBrokerLogin()) {
    // cancel the current navigation because we are redirecting to external auth-broker
    next(false)
    try {
      return await auth.signInWithAuthBroker()
    } catch {
      // ignore, fallback to local login
    }
  }

  if (to.name === "login") return next()
  return next({path: "/login"})
}

router.beforeEach(async (to, from, next) => {
  const wantsLocalLogin = to.name == "login" || to.name == "lti-login"
  const user = store.state.auth.loginUser || await auth.getLoginUser()
  const isLoggedIn = !!user

  if(isLoggedIn) {
    const postAuthRedirectPath = popPostAuthRedirectPath()
    if(postAuthRedirectPath && postAuthRedirectPath !== to.fullPath) {
      return next({path: postAuthRedirectPath})
    }
    if(wantsLocalLogin) return next({path: "/"})
    return next()
  }

  // not logged in — redirect to one of appropriate logins
  await handleRedirectLogin(to, next)
})

export default router
